import { createAsyncThunk, isAnyOf, createSlice } from '@reduxjs/toolkit';
import { AUTH_TOKEN_RESPONSE_HEADER } from '../constants';
import { GroupIdMappingQuery } from '../generated-interfaces/graphql';
import { resetAI } from '../redux/features/ai/ai.slice';
import { UserDetails } from '../types';
import { get, post } from '../utils/api';
import { GROUP_ID_MAPPING_DEFAULT_RESTAURANT } from '../utils/constants';
import inMemoryGroupIdMapping from '../utils/inMemoryGroupIdMapping';
import { getAuthToken, saveUserState } from '../utils/local-storage';
import logger from '../utils/logger';
import { getGraphQLClient, setAuthToken } from '../utils/network';
import { PerfTimer } from '../utils/timer';
import { ERROR_ACTIONS } from './errorSlice';
import {
  resetHypothesisFrame,
  sendNetworkCallLogs,
  setIsAIActive,
} from './messagingSlice';
import { restaurantsByUserRole } from './restaurantSlice';
import { getAuthRefactorFeatureFlag } from '../utils/session-storage';
import { updateCriticalCheckScore } from '../redux/features/healthScore/healthScore.slice';
import { CriticalChecksNames } from '../redux/features/healthScore/healthScore.constants';

export interface UserState {
  userProfile: UserDetails | null;
  isLoggedIn: boolean;
  token?: string | null;
  didAttemptAuthCheck: boolean;
}

export const initialUserState: UserState = {
  userProfile: null,
  isLoggedIn: false,
  token: null,
  didAttemptAuthCheck: false,
};

export const login = createAsyncThunk(
  'user/login',
  async (
    { email, password }: { email: string; password: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    const {
      config: { AUTH_URL },
    } = getState();
    try {
      const response = await post({
        url: `${AUTH_URL}/login`,
        data: {
          email: email.trim(),
          password,
        },
      });
      const { data, headers = {} } = response || {};

      logger.log({
        message: 'AUTH login: Setting auth token',
        userEmail: email,
      });
      setAuthToken(headers[AUTH_TOKEN_RESPONSE_HEADER]);

      dispatch(restaurantsByUserRole());
      dispatch(ERROR_ACTIONS.clearErrors());
      dispatch(setIsAIActive(false));
      dispatch(resetHypothesisFrame());
      dispatch(resetAI());
      dispatch(
        updateCriticalCheckScore({
          name: CriticalChecksNames.apiLogin,
          value: 1,
        })
      );
      return data;
    } catch (error: any) {
      logger.error({
        message: 'AUTH error: Failed to login',
        moreInfo: {
          errorCode: error?.response?.status,
          errorMessage: error?.message,
        },
      });
      dispatch(
        updateCriticalCheckScore({
          name: CriticalChecksNames.apiLogin,
          value: 0,
        })
      );
      return rejectWithValue(error.message);
    }
  }
);

export const initialAuthCheck = createAsyncThunk(
  'user/initialAuthCheck',
  async (val: boolean, { dispatch, getState, rejectWithValue }) => {
    const {
      config: { AUTH_URL },
    } = getState();

    const authToken = getAuthToken();
    if (!authToken) {
      logger.error({
        message: 'AUTH error: No auth token found in initialAuthCheck',
      });

      return rejectWithValue(
        new Error('No auth token found in initialAuthCheck')
      );
    }

    try {
      const response = await get({
        url: `${AUTH_URL}/login/status`,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const { data, headers = {} } = response || {};
      logger.log({ message: 'AUTH initialAuthCheck: Setting auth token' });
      setAuthToken(headers[AUTH_TOKEN_RESPONSE_HEADER]);
      dispatch(restaurantsByUserRole());
      dispatch(fetchGroupIdMapping(GROUP_ID_MAPPING_DEFAULT_RESTAURANT));
      return data;
    } catch (error: any) {
      logger.error({
        message: 'AUTH error: Failed to perform initialAuthCheck',
        moreInfo: {
          errorCode: error?.response?.status,
          errorMessage: error?.message,
        },
      });
      return rejectWithValue(error);
    }
  }
);

/**
 * This is called every 30 minutes so that new JWT token is received and in the apiCallWrapper the new JWT value is stored
 */
export const refreshAuthToken = createAsyncThunk(
  'user/refreshAuthToken',
  async ({ logout }: { logout: Function }, { getState, rejectWithValue }) => {
    const {
      config: { AUTH_URL },
      restaurant: {
        selectedRestaurantDetails: { restaurantCode },
      },
    } = getState();

    const authToken = getAuthToken();
    if (!authToken) {
      logger.error({
        message: 'AUTH error: No auth token found in refreshAuthToken',
      });
      if (getAuthRefactorFeatureFlag()) {
        logout('Error while fetching access token. Logging out');
        return;
      } else {
        return rejectWithValue(
          new Error('No auth token found while executing refreshAuthToken')
        );
      }
    }

    let url = `${AUTH_URL}/login/status?`;
    if (restaurantCode) {
      url += `restaurant_code=${restaurantCode}`;
    }
    logger.log({
      message: 'AUTH refreshAuthToken: Refreshing auth token',
      restaurantCode,
    });

    try {
      const response = await get({
        url,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const { headers = {} } = response || {};
      logger.log({
        message: 'AUTH refreshAuthToken: Setting auth token',
        restaurantCode,
      });
      setAuthToken(headers[AUTH_TOKEN_RESPONSE_HEADER]);
    } catch (error: any) {
      logger.error({
        message: 'AUTH error: Failed to get new auth token in refreshAuthToken',
        moreInfo: {
          errorCode: error?.response?.status,
          errorMessage: error?.message,
        },
      });
      if (getAuthRefactorFeatureFlag()) {
        logout('Error while fetching refresh token. Logging out');
        return;
      } else {
        return rejectWithValue(error);
      }
    }
  }
);

export const fetchGroupIdMapping = createAsyncThunk(
  'user/fetchGroupIdMapping',
  async (restaurantCode: string, { dispatch, rejectWithValue, getState }) => {
    const {
      config: { PRP_API },
    } = getState();
    const sdk = getGraphQLClient(PRP_API);
    const requestTimer = new PerfTimer();
    try {
      const data =
        ((await sdk.groupIdMapping({
          // hard-coding the restaurant code as we have only one restaurant with this requirement.
          restaurantCode,
        })) as any as GroupIdMappingQuery) || {};
      if (data) {
        const { queryCKEMenu = '{}' } = data;
        inMemoryGroupIdMapping.setGroupIdMappingList(
          restaurantCode,
          queryCKEMenu ? JSON.parse(queryCKEMenu) : {}
        );
      }

      dispatch(
        sendNetworkCallLogs({
          url: PRP_API,
          graphQLOperation: 'groupIdMapping',
          duration: requestTimer.stop(),
          message: 'api request succeeded',
          via: 'graphql_sdk',
        })
      );
    } catch (error: any) {
      logger.error({
        message: 'GraphQL error: Failed to get group id mapping',
        error: error?.response?.errors,
      });

      dispatch(
        sendNetworkCallLogs({
          url: PRP_API,
          graphQLOperation: 'groupIdMapping',
          duration: requestTimer.stop(),
          message: 'api request failed',
          via: 'graphql_sdk',
        })
      );

      return rejectWithValue(error?.response?.errors);
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    logout: (state) => {
      state.userProfile = null;
      state.isLoggedIn = false;
      if (!getAuthRefactorFeatureFlag()) {
        state.token = null;
      }
      logger.log('Setting auth token during logout');
      setAuthToken(null);
      saveUserState(state);
      logger.clearLogs();
    },
    // This function is not used. Can be deleted
    authCheckSuccess: (state, action) => {
      if (state.userProfile && !getAuthRefactorFeatureFlag()) {
        state.token = action.payload.authorizationToken;
        state.userProfile.authorizationToken =
          action.payload.authorizationToken;
        saveUserState(state);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initialAuthCheck.rejected, (state, action) => {
      state.isLoggedIn = false;
      state.didAttemptAuthCheck = true;
    });
    builder.addMatcher(
      isAnyOf(login.fulfilled, initialAuthCheck.fulfilled),
      (state, action) => {
        if (getAuthRefactorFeatureFlag()) {
          const { id, username, firstName, lastName, email } = action.payload;

          state.userProfile = {
            id: id + '',
            username,
            email,
            firstName,
            lastName,
          };
        } else {
          state.userProfile = {
            ...action.payload,
            id: action.payload.id + '',
          };
        }

        state.isLoggedIn = true;
        state.didAttemptAuthCheck = true;
        saveUserState(state);
        logger.clearLogs();
      }
    );
  },
});

export const userActions = userSlice.actions;

export default userSlice.reducer;
